"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Plus, MonitorSmartphone, Fingerprint, ChartLine } from "lucide-react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const disciplines = [{
  id: "01",
  title: "Architecture",
  description: "Structural planning and architectural design for residential and commercial projects, ensuring modern functionality and timeless aesthetics.",
  icon: MonitorSmartphone
}, {
  id: "02",
  title: "Interior Design",
  description: "Bespoke interior solutions that combine creative vision with technical expertise to create purposeful and high-quality living spaces.",
  icon: Fingerprint
}, {
  id: "03",
  title: "Construction",
  description: "Professional execution and project management for turnkey solutions, from initial renovation to final high-quality delivery.",
  icon: ChartLine
}];
export default function OurDisciplines() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="221620" id="our-disciplines" className="py-24 lg:py-32 bg-white text-onyx relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {}
        <div ref={headerRef} className={cn("mb-20 md:mb-28 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="flex items-center gap-4 mb-6">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gold-ochre bg-gold-ochre/10 px-2 py-1 rounded-md">
              Disciplines
            </span>
            <div className="h-[1px] w-12 bg-concrete" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-[1.1]">
            Creating spaces with <span className="text-gold-ochre">purpose</span> through technical excellence.
          </h2>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-concrete">
          {disciplines.map((item, i) => {
          const {
            ref: itemRef,
            isVisible: isItemVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          return <div key={item.id} data-index={i} ref={itemRef} className={cn("group relative p-8 lg:p-12 border-b md:border-r border-concrete last:md:border-r-0 transition-all duration-1000 ease-out", isItemVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="flex justify-between items-start mb-12">
                  <span className="font-mono text-xs text-concrete tracking-tighter">
                    REF_{item.id}
                  </span>
                  <item.icon className="w-6 h-6 text-concrete transition-colors duration-300" strokeWidth={1.5} />
                </div>

                {}
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold tracking-tight uppercase">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm md:text-base max-w-xs">
                    {item.description}
                  </p>
                </div>

                {}
                <div className="mt-16">
                  <Link to="/HomePage" className="inline-flex items-center gap-3 group/link">
                    <div className="w-10 h-10 rounded-md border border-concrete flex items-center justify-center transition-all duration-300">
                      <Plus className="w-4 h-4 text-onyx transition-colors" />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest font-bold transition-colors">
                      Explore Work
                    </span>
                  </Link>
                </div>

                {}
                <div className="absolute inset-0 bg-gold-ochre/0 transition-colors duration-500 pointer-events-none" />
              </div>;
        })}
        </div>

        {}
        <div className="mt-12 flex justify-end">
          <span className="font-mono text-[10px] text-concrete uppercase tracking-widest">
            ArchiForma © 2007-2024 / Structural Integrity Verified
          </span>
        </div>
      </div>
    </section>;
}